import React, {useCallback, useState} from "react";
import {useNavigate} from "react-router-dom";
import axios from "axios";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {
    CheckboxText,
    CheckboxWrapper,
    Header,
    Row,
    StyledButton,
    StyledCard,
    StyledForm,
    StyledLink,
    StyledLinkWrapper,
    StyledTextField,
    Subtext
} from "./Register.styled";
import {Checkbox, CircularProgress, IconButton, InputAdornment} from "@mui/material";
import Logo from "../../../assets/images/taia_logo.png";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {AuthContainer} from "../Auth.styled";
import {usePageTitle} from '../../../hooks/usePageTitle';
import LogoWithLanguageSwitcher from '../../../components/common/LogoWithLanguageSwitcher';
import {PageTitleKeys} from '../../../config/routeConstants';
import {parseError} from "../../../services/http.service";
import {useTranslation} from 'react-i18next';
import { validatePassword } from "../../../utils/password-validation";

async function registerUser(firstName: string, lastName: string, position: string, phoneNumber: string, email: string, password: string) {
    try {
        await axios.post(`/auth/register`, {
            firstName,
            lastName,
            position,
            phoneNumber,
            email,
            password,
        });
        return true;
    } catch (error) {
        console.error("Błąd rejestracji:", error);
        return false;
    }
}

const Register: React.FC = () => {
    usePageTitle(PageTitleKeys.Register);
    const {t} = useTranslation();
    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [position, setPosition] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [consent, setConsent] = useState(false);
    const [isFetchingToken, setIsFetchingToken] = useState(false);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const {showSnackbar} = useSnackbar();


    const handleRegister = useCallback(async () => {
        if (!consent) return;
        if (passwordError) return;
        setIsFetchingToken(true);
        try {
            const passed = await registerUser(name, surname, position, phoneNumber, email, password);
            if (passed) {
                showSnackbar(t('register.successMessage'), 'success');
                navigate("/thank-you-for-registration");
            } else {
                showSnackbar(t('register.errorMessage'), 'error');
            }
        } catch (error) {
            console.error('Error during registration:', error);
            showSnackbar(parseError(error), "error");
        } finally {
            setIsFetchingToken(false);
        }
    }, [consent, name, surname, position, phoneNumber, email, password, passwordError, showSnackbar, navigate,  t]);

    return (
        <AuthContainer role="region" aria-labelledby="register-title">
            <LogoWithLanguageSwitcher src={Logo} alt={t('auth.logo.alt')} clickable={false} />
            <StyledCard>
                <Header id="register-title">{t('register.title')}</Header>
                <Subtext>{t('register.welcome')}</Subtext>

                <StyledForm autoComplete="off" aria-live="polite" aria-atomic={true} aria-busy={isFetchingToken}>
                    <Row>
                        <div style={{width: '100%'}}>
                            <StyledTextField
                                label={t('register.firstName')}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                fullWidth
                                disabled={isFetchingToken}
                                inputProps={{ 'aria-label': t('register.firstName.ariaLabel') as string }}
                            />
                            <div style={{height: '24px', marginTop: '4px'}}></div>
                        </div>
                        <div style={{width: '100%'}}>
                            <StyledTextField
                                label={t('register.lastName')}
                                value={surname}
                                onChange={(e) => setSurname(e.target.value)}
                                fullWidth
                                disabled={isFetchingToken}
                                inputProps={{ 'aria-label': t('register.lastName.ariaLabel') as string }}
                            />
                            <div style={{height: '24px', marginTop: '4px'}}></div>
                        </div>
                    </Row>
                    <Row>
                        <div style={{width: '100%'}}>
                            <StyledTextField
                                label={t('register.position')}
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                                fullWidth
                                disabled={isFetchingToken}
                                inputProps={{ 'aria-label': t('register.position.ariaLabel') as string }}
                            />
                            <div style={{height: '24px', marginTop: '4px'}}></div>
                        </div>
                        <div style={{width: '100%'}}>
                            <StyledTextField
                                label={t('register.phoneNumber')}
                                type="tel"
                                inputProps={{
                                    inputMode: "numeric",
                                    pattern: "^\\+?[0-9]*$",
                                    maxLength: 15,
                                    'aria-label': t('register.phoneNumber.ariaLabel') as string
                                }}
                                value={phoneNumber}
                                onChange={(e) => {
                                    const cleaned = e.target.value.replace(/[^+\d]/g, "");
                                    setPhoneNumber(cleaned);
                                }}
                                fullWidth
                                variant="outlined"
                            />
                            <div style={{height: '24px', marginTop: '4px'}}></div>
                        </div>
                    </Row>
                    <div>
                        <StyledTextField
                            label={t('register.email')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{ 'aria-label': t('register.email.ariaLabel') as string }}
                        />
                        <div style={{height: '24px', marginTop: '4px'}}></div>
                    </div>
                    <div>
                        <StyledTextField
                            label={t('register.password')}
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setPasswordError("");
                            }}
                            onBlur={() => {
                                const error = validatePassword(password, t);
                                setPasswordError(error || "");
                            }}
                            error={!!passwordError}
                            helperText={passwordError}
                            fullWidth
                            disabled={isFetchingToken}
                            inputProps={{ 'aria-label': t('register.password.ariaLabel') as string }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            edge="end"
                                            sx={{color: 'primary.main'}}
                                            disabled={isFetchingToken}
                                            aria-label={t('register.password.toggleVisibility') as string}
                                        >
                                            {showPassword ? <VisibilityOff/> : <Visibility/>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <div style={{height: '24px', marginTop: '4px'}}></div>
                    </div>
                    <CheckboxWrapper>
                        <Checkbox
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            sx={{padding: "0 8px 0 0"}}
                            disabled={isFetchingToken}
                            inputProps={{ 'aria-label': t('register.consent.ariaLabel') as string }}
                        />
                        <CheckboxText>
                            {t('register.consent')}
                        </CheckboxText>
                    </CheckboxWrapper>
                    {isFetchingToken && (
                        <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                            {t('register.loading')}
                        </div>
                    )}
                    <StyledButton onClick={handleRegister}
                                  disabled={!consent || isFetchingToken || !name || !surname || !email || !password}>
                        {isFetchingToken ? <CircularProgress size={24} sx={{color: 'inherit'}}/> : t('register.registerButton')}
                    </StyledButton>
                    <StyledLinkWrapper>
                        <span>{t('register.loginText')}</span>
                        <StyledLink
                            onClick={!isFetchingToken ? () => navigate("/login") : undefined}
                            disabled={isFetchingToken}
                        >
                            <strong>{t('register.loginLink')}</strong>
                        </StyledLink>
                    </StyledLinkWrapper>
                </StyledForm>
            </StyledCard>
        </AuthContainer>
    );
};

export default Register;
